import { bindingMode, computedFrom } from "aurelia-binding";
import { autoinject, bindable } from "aurelia-framework";

import nameof from "../../../common/nameof";
import { IElement, IGrouping } from "../../../interfaces/form-builder/i-element";
import { IAncestorSettings } from "../../../interfaces/form-builder/i-input-options";
import { NoteDataManager } from "../note-data-manager";
import { getColumnClasses } from "../../../common/utilities/column-classes";
import { cleanElementCode, setElementPillColor } from "../../../common/utilities/note-pill-formatting";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { ScrubberResultPublishKey } from "../form-navigator/form-navigator";
import { IScrubberResult } from "../../../interfaces/form-builder/i-scrubber";

@autoinject
export class SubSectionGroup {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public element: IElement;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public parentName: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public ancestorSettings: IAncestorSettings;
    private _noteDataManager: NoteDataManager;
    private _ea: EventAggregator;
    private _eaSubscriptions: Subscription[] = [];
    public tooltip: string;
    public hasError: boolean = false;
    public hasWarning: boolean = false;
    public hasInconsistency: boolean = false;
    public hasHighlight: string = "";

    @computedFrom(nameof<SubSectionGroup>("element"))
    public get columnsClasses(): string {
        let columns = this.element.columns;
        return getColumnClasses(columns);
    }

    public constructor(ea: EventAggregator, noteDataManager: NoteDataManager) {
        this._noteDataManager = noteDataManager;
        this._ea = ea;
    }

    public attached() {
        this.tooltip = this._noteDataManager.getTooltip(this.element.name);
        this.initSubscriptions();
        if (!!this.element.code) {
            this.getScrubberResults();
        }
    }

    private initSubscriptions() {
        if (this.element.code) {
            this._eaSubscriptions.push(this._ea.subscribe(ScrubberResultPublishKey, () => this.getScrubberResults()));
            this._eaSubscriptions.push(
                this._ea.subscribe("Highlight Form Input", (question: { code: string; type: string }) => {
                    if (question.code === cleanElementCode(this.element as IGrouping)) {
                        this.hasHighlight = question.type;
                    }
                })
            );
            this._eaSubscriptions.push(
                this._ea.subscribe("Reset :: Highlight Form Input", () => {
                    this.hasHighlight = "";
                })
            );
        }
    }

    private getScrubberResults() {
        let scrubberResults = this._noteDataManager.getScrubberResults();
        if (
            scrubberResults?.errors.length > 0 ||
            scrubberResults?.warnings.length > 0 ||
            scrubberResults?.inconsistencies.length > 0
        ) {
            this.hasError = this.hasProblem(scrubberResults.errors);
            this.hasWarning = this.hasProblem(scrubberResults.warnings);
            this.hasInconsistency = this.hasProblem(scrubberResults.inconsistencies);
        }
    }

    private hasProblem(result: IScrubberResult[]) {
        return result.some((item) => item.questions.indexOf(cleanElementCode(this.element as IGrouping)) > -1);
    }

    public checkColor() {
        return setElementPillColor(this.element as IGrouping);
    }

    public checkCode() {
        return cleanElementCode(this.element as IGrouping);
    }

    public detached() {
        this._eaSubscriptions?.forEach((sub) => sub?.dispose());
    }
}
